import { dedupeTimelineEntries } from '../../services/timeline/timelineDeduping';
import { isDeletedTimelineEntry } from '../../services/timeline/deletionRules';
import { getDayDateRange, isTimelineEntryVisibleOnDate } from '../../services/timeline/dateUtils';
import { formatTimelineTime, toTimelineDate } from "../timeline/timelineDateUtils";
import { isBehaviorIncidentEntry } from "../../constants/logTypeRegistry";

const toEntryDate = toTimelineDate;
const formatTimeForSummary = formatTimelineTime;

const isIncidentLikeEntry = (entry = {}) => Boolean(
  entry?.collection === 'incidents'
  || entry?.incidentStyle
  || entry?.entryType === 'incident'
  || entry?.timelineType === 'incident'
  || isBehaviorIncidentEntry(entry)
);

const getActivityStreak = (entries = []) => {
  if (!entries.length) return 0;

  let streak = 0;
  const today = new Date();

  for (let i = 0; i < 30; i++) {
    const checkDate = new Date(today);
    checkDate.setDate(today.getDate() - i);
    const dayStart = new Date(checkDate.getFullYear(), checkDate.getMonth(), checkDate.getDate());
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

    const hasEntryForDay = entries.some((entry) => {
      const entryDate = toEntryDate(entry.timestamp);
      return !Number.isNaN(entryDate.getTime()) && entryDate >= dayStart && entryDate < dayEnd;
    });

    if (hasEntryForDay) {
      streak++;
    } else if (i > 0) {
      break;
    }
  }

  return streak;
};

const serializeEntriesByChild = (entriesByChild = {}) => {
  const normalized = Object.keys(entriesByChild)
    .sort()
    .reduce((acc, childId) => {
      acc[childId] = (entriesByChild[childId] || []).map((entry) => ({
        id: entry.id,
        timestamp: toEntryDate(entry.timestamp).getTime(),
        type: entry.type,
        collection: entry.collection || null,
        sourceNoteId: entry.sourceNoteId || entry.metadata?.sourceNoteId || entry.source?.sourceNoteId || null,
        plannerCardKey: entry.plannerCardKey || entry.metadata?.timelinePlanner?.plannerCardKey || null,
        sourceContainerKind: entry.sourceContainerKind || entry.metadata?.timelinePlanner?.sourceContainerKind || null,
        processingStatus: entry.processingStatus || entry.metadata?.processingStatus || entry.metadata?.careSourceProcessing?.status || null,
        parsedObservation: Boolean(entry.parsedObservation || entry.source?.parsedObservation || entry.metadata?.parsedObservation),
      }));
      return acc;
    }, {});

  return JSON.stringify(normalized);
};

const serializeSummaryByChild = (summaryByChild = {}) => {
  const normalized = Object.keys(summaryByChild)
    .sort()
    .reduce((acc, childId) => {
      const summary = summaryByChild[childId] || {};
      acc[childId] = {
        totalEntries: summary.totalEntries || 0,
        todayCount: summary.todayCount || 0,
        weekCount: summary.weekCount || 0,
        activityStreak: summary.activityStreak || 0,
        lastActivityTime: summary.lastActivityTime || null,
      };
      return acc;
    }, {});

  return JSON.stringify(normalized);
};

const mergeEntriesForChild = (timelineEntries = [], incidentEntries = []) => {
  return dedupeTimelineEntries([...timelineEntries, ...incidentEntries]
    .filter(entry => entry?.id && !isDeletedTimelineEntry(entry)));
};

const getIncidentEntriesForChild = (entries = []) => entries.filter((entry) => isIncidentLikeEntry(entry));

const buildDashboardTimelineStateForChild = (entries = []) => {
  entries = mergeEntriesForChild(entries);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const { start, end } = getDayDateRange(todayStart);
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);

  const todaysEntries = entries
    .filter(entry => isTimelineEntryVisibleOnDate(entry, todayStart, start, end))
    .sort((a, b) => {
      const aTime = toEntryDate(a.timestamp);
      const bTime = toEntryDate(b.timestamp);
      return bTime - aTime;
    });

  const weekEntries = entries.filter((entry) => {
    const entryDate = toEntryDate(entry.timestamp);
    return !Number.isNaN(entryDate.getTime()) && entryDate >= weekAgo;
  });

  const fullTimelineEntries = [...entries];
  const recentTimelineEntries = entries
    .filter((entry) => {
      const entryDate = toEntryDate(entry.timestamp);
      return !Number.isNaN(entryDate.getTime()) && entryDate >= weekAgo;
    })
    .sort((a, b) => toEntryDate(b.timestamp) - toEntryDate(a.timestamp))
    .slice(0, 5);

  return {
    allEntries: mergeEntriesForChild(fullTimelineEntries),
    recentEntries: recentTimelineEntries,
    incidents: getIncidentEntriesForChild(fullTimelineEntries),
    summary: {
      totalEntries: todaysEntries.length,
      todayCount: todaysEntries.length,
      weekCount: weekEntries.length,
      activityStreak: getActivityStreak(entries),
      lastActivityTime: todaysEntries[0] ? formatTimeForSummary(todaysEntries[0]) : null,
    },
  };
};

export {
  buildDashboardTimelineStateForChild,
  formatTimeForSummary,
  getIncidentEntriesForChild,
  mergeEntriesForChild,
  serializeEntriesByChild,
  serializeSummaryByChild,
  toEntryDate,
};
